import { useState } from "react";
import { useModuleData } from "@/hooks/useModuleData";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { DataTable } from "@/components/common/DataTable";
import { Loader } from "@/components/common/Loader";
import { eur, shortDate } from "@/lib/format";
import { Calculator } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

const C = "#06B6D4";

export default function Controlling() {
  const { data, loading } = useModuleData("/co/cost-center-report");
  const [sel, setSel] = useState("");
  if (loading || !data) return <Loader />;

  const ccs = data.cost_centers || [];
  const totalPlan = data.totals?.plan || 0;
  const totalActual = data.totals?.actual || 0;
  const variance = totalPlan - totalActual;
  const chart = ccs.map((c) => ({ name: c.name, Plan: c.plan, Actual: c.actual }));
  const lines = (data.line_items || []).filter((l) => !sel || l.cc_code === sel);

  const lineCols = [
    { key: "cc_code", label: "Cost Centre", render: (r) => <span className="font-mono text-xs text-[#06B6D4]">{r.cc_code}</span> },
    { key: "account_name", label: "Cost Element", render: (r) => (<div><div>{r.account_name}</div><div className="font-mono text-[10px] text-[#6a6d70]">{r.account}</div></div>) },
    { key: "doc_no", label: "Document", render: (r) => <span className="font-mono text-xs text-[#6a6d70]">{r.doc_no}</span> },
    { key: "posting_date", label: "Date", render: (r) => shortDate(r.posting_date) },
    { key: "amount", label: "Amount", render: (r) => <span className="font-mono">{eur(r.amount)}</span> },
  ];

  return (
    <div>
      <PageHeader title="Controlling" subtitle="Cost-centre accounting — plan vs actual, variance analysis and cost-element line items (EP-CO01 · S/4 S_ALR)." icon={Calculator} accent={C} />
      <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Plan Cost" value={eur(totalPlan)} accent={C} />
        <KpiCard label="Actual Cost" value={eur(totalActual)} accent="#F97316" />
        <KpiCard label="Variance" value={eur(variance)} sub={variance >= 0 ? "under budget" : "over budget"} accent={variance >= 0 ? "#10B981" : "#EF4444"} />
        <KpiCard label="Cost Centres" value={ccs.length} accent={C} />
      </div>

      <div className="rounded-xl border border-[#e5e9f0] bg-white p-5 shadow-sm">
        <h3 className="text-lg font-semibold text-[#32363a]">Cost Centre — Plan vs Actual</h3>
        <div className="mt-4 h-80" style={{ minHeight: 320 }}>
          <ResponsiveContainer width="100%" height="100%" minHeight={280}>
            <BarChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e9f0" />
              <XAxis dataKey="name" stroke="#64748B" fontSize={11} />
              <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `£${Math.round(v / 1000)}k`} />
              <Tooltip contentStyle={{ background: "#eef1f5", border: "1px solid #e5e9f0", borderRadius: 8, color: "#32363a" }} formatter={(v) => eur(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Plan" fill="#06B6D4" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Actual" fill="#F97316" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {ccs.map((c) => {
          const over = c.util_pct > 100;
          return (
            <button key={c.cc_code} data-testid={`cc-card-${c.cc_code}`} onClick={() => setSel(sel === c.cc_code ? "" : c.cc_code)}
              className={`rounded-xl border bg-white p-4 text-left shadow-sm transition-all hover:shadow-md ${sel === c.cc_code ? "border-[#06B6D4] ring-1 ring-[#06B6D4]" : "border-[#e5e9f0]"}`}>
              <div className="flex items-center justify-between">
                <div><div className="font-mono text-xs text-[#06B6D4]">{c.cc_code}</div><div className="text-[#32363a]">{c.name}</div></div>
                <span className={`font-mono text-sm ${over ? "text-red-600" : "text-emerald-600"}`}>{c.util_pct}%</span>
              </div>
              <div className="mt-3 h-2 rounded bg-[#eef1f5]"><div className={`h-2 rounded ${over ? "bg-red-500" : "bg-[#06B6D4]"}`} style={{ width: `${Math.min(100, c.util_pct)}%` }} /></div>
              <div className="mt-2 flex justify-between text-[11px] text-[#6a6d70]"><span>Actual {eur(c.actual)}</span><span>Var {eur(c.variance)}</span></div>
            </button>);
        })}
      </div>

      <div className="mt-6">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">Cost-Element Line Items{sel ? ` · ${sel}` : " · All"}</h3>
          {sel && <button data-testid="cc-clear-filter" onClick={() => setSel("")} className="text-xs text-[#0a6ed1] hover:underline">Clear filter</button>}
        </div>
        <DataTable testId="cc-line-items-table" columns={lineCols} rows={lines} />
      </div>
    </div>
  );
}
